import React from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { format } from "date-fns";
import LoadingIndicator from "@/components/LoadingIndicator/LoadingIndicator";
import {
  isPreEpRegistrationStepSlug,
  PRE_EP_REGISTRATION_STEPS,
} from "../../config/preEpRegistrationSteps";
import { PRE_EP_HOME } from "../../config/preEpStage";
import { usePreEpChecklist } from "../../hooks/usePreEpChecklist";
import { PreEpStepEvent } from "../../api/fetchPreEpChecklist";
import { ArrowIcon, preEpCard } from "../PreEpUi/PreEpUi";

const FORMAT_LABELS: Record<NonNullable<PreEpStepEvent["format"]>, string> = {
  online: "Online",
  in_person: "In person",
};

/**
 * "Choose your Executive Perspective" (or welcome session): the events
 * marked for the step. With only one, it goes straight to its sessions.
 */
const PreEpEventChoice: React.FC = () => {
  const { step } = useParams<{ step: string }>();
  const { checklist, loading } = usePreEpChecklist();

  if (!isPreEpRegistrationStepSlug(step)) {
    return <Navigate replace to={PRE_EP_HOME} />;
  }
  const config = PRE_EP_REGISTRATION_STEPS[step];

  if (loading || !checklist) {
    return <LoadingIndicator />;
  }
  const events = checklist[config.checklistKey].events;
  if (events.length === 1) {
    return (
      <Navigate
        replace
        to={`/pre-ep/register/${step}/events/${events[0].uuid}`}
      />
    );
  }

  return (
    <div className="relative mx-auto flex max-w-[1000px] flex-col gap-8 px-5 pb-16 pt-8 md:px-11 md:pt-12">
      <BackToSteps />
      <header className="flex flex-col gap-2">
        <h1 className="m-0 text-[30px] font-light md:text-[44px]">
          {config.title}
        </h1>
        <p className="m-0 text-lg text-[#C3CBE0]">{config.subtitle}</p>
      </header>
      {events.length === 0 ? (
        <p className={`${preEpCard} m-0 text-[15px] text-[#D3D9EA]`}>
          Registration isn&rsquo;t open yet. Check back soon, or reach out to
          your coach.
        </p>
      ) : (
        <ul className="m-0 grid list-none gap-4 p-0 md:grid-cols-2">
          {events.map((event) => (
            <li key={event.uuid}>
              <Link
                className={`${preEpCard} flex h-full flex-col gap-3 text-white no-underline transition-colors hover:border-[#5A6BA0] hover:bg-[#1B274B] hover:text-white hover:no-underline`}
                // Tells the dates page that Back should return here.
                state={{ fromEventChoice: true }}
                to={`/pre-ep/register/${step}/events/${event.uuid}`}
              >
                {event.format && (
                  <span className="w-fit rounded-full border border-[#2C3A66] bg-[#111D40] px-2.5 py-0.5 text-xs font-extrabold uppercase tracking-[0.06em] text-[#FF8AA3]">
                    {FORMAT_LABELS[event.format]}
                  </span>
                )}
                <h2 className="m-0 text-[22px] font-bold">{event.name}</h2>
                <p className="m-0 line-clamp-3 text-[15px] leading-[1.55] text-[#D3D9EA]">
                  {event.description.replace(/<[^>]+>/g, " ")}
                </p>
                <p className="m-0 mt-auto text-sm text-[#C3CBE0]">
                  {event.nextSessionStart
                    ? `Next: ${format(new Date(event.nextSessionStart), "MMM d, yyyy")} · ${event.upcomingSessionCount} upcoming ${event.upcomingSessionCount === 1 ? "date" : "dates"}`
                    : "No upcoming dates yet"}
                </p>
                <span className="inline-flex items-center gap-2 text-[15px] font-bold">
                  Choose this <ArrowIcon />
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const BackToSteps: React.FC<{ to?: string; label?: string }> = ({
  to = PRE_EP_HOME,
  label = "Back to your steps",
}) => (
  <Link
    className="inline-flex w-fit items-center gap-2 text-[15px] font-semibold text-[#C3CBE0] no-underline hover:text-white hover:no-underline"
    to={to}
  >
    <svg
      aria-hidden="true"
      fill="none"
      height="16"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2.2"
      viewBox="0 0 24 24"
      width="16"
    >
      <path d="M19 12H5M11 6l-6 6 6 6" />
    </svg>
    {label}
  </Link>
);

export default PreEpEventChoice;
